// @ts-strict-ignore
import React, { useContext, useMemo } from 'react';

import { RuleEditorContext } from '../../NodeEditorDrawer/context';
import { CaretPositionSuffixTypes, RuleEditorDropdownMode } from '../../NodeEditorDrawer/types';
import { Caret, HiddenCaret, HoverButton } from './styles';
import { CaretIndexType } from '../../useCaretControl/constants';
import { getIsCaretPositionInInitialInsertionMode } from '../../useCaretControl/helpers';
import { ButtonSize, ButtonVariant } from '@usealloy/component-library';

type CaretRuleEditorDropdownMode = (
  RuleEditorDropdownMode.CARET_THRESHOLD
  | RuleEditorDropdownMode.GROUP_START
  | RuleEditorDropdownMode.GROUP_END
);

type ChildrenProps = {
  caretDropdownMode: CaretRuleEditorDropdownMode;
};

function MorphingCaret({
  children,
  position,
  parentRulePath,
  mode,
  inlineSuggestion,
}: {
  children: ({ caretDropdownMode }: ChildrenProps) => React.ReactNode;
  position: string | undefined;
  parentRulePath: string;
  mode: string;
  inlineSuggestion?: boolean;
}) {
  const {
    morphingCaretPosition,
    setMorphingCaretPosition,
    getFlattenedCaretPositionIndex,
  } = useContext(RuleEditorContext);

  const parentCaretPosition = useMemo(() => {
    if (position.endsWith(CaretPositionSuffixTypes.THRESHOLD_LEFT_ACTION)
      || position.endsWith(CaretPositionSuffixTypes.RULE_OPERATOR_OR)) {
      return getFlattenedCaretPositionIndex({
        itemPath: parentRulePath,
        type: CaretIndexType.RULES,
      });
    }

    return position;
  }, [getFlattenedCaretPositionIndex, parentRulePath, position]);

  const isCaretActivatedForAction = useMemo(() => {
    switch (mode) {
      case CaretPositionSuffixTypes.GROUP_START: {
        if (position.endsWith(CaretPositionSuffixTypes.GROUP_START)) {
          return morphingCaretPosition === `${parentCaretPosition}${CaretPositionSuffixTypes.CARET_INSERTION}`;
        }
        return morphingCaretPosition === `${parentCaretPosition}${CaretPositionSuffixTypes.GROUP_START}${CaretPositionSuffixTypes.CARET_INSERTION}`;
      }
      case CaretPositionSuffixTypes.GROUP_END: {
        return morphingCaretPosition === `${parentCaretPosition}${CaretPositionSuffixTypes.GROUP_END}${CaretPositionSuffixTypes.CARET_INSERTION}`;
      }
      default: {
        return morphingCaretPosition === `${position}${CaretPositionSuffixTypes.CARET_INSERTION}`;
      }
    }
  }, [mode, morphingCaretPosition, position, parentCaretPosition]);


  const caretDropdownMap = {
    [CaretPositionSuffixTypes.GROUP_START]: RuleEditorDropdownMode.GROUP_START,
    [CaretPositionSuffixTypes.GROUP_END]: RuleEditorDropdownMode.GROUP_END,
  };

  const caretDropdownMode = (
    caretDropdownMap[mode] || RuleEditorDropdownMode.CARET_THRESHOLD
  ) as CaretRuleEditorDropdownMode;

  const caretInlineSuggestionMap = {
    [CaretPositionSuffixTypes.GROUP_START]: '(',
    [CaretPositionSuffixTypes.GROUP_END]: ')',
  };

  const caretInlineSuggestionString = inlineSuggestion && caretInlineSuggestionMap[mode]
    ? caretInlineSuggestionMap[mode]
    : '';

  const onClickHiddenCaret = () => {
    let newPosition = `${position}${CaretPositionSuffixTypes.CARET_INSERTION}`;
    if (mode === CaretPositionSuffixTypes.GROUP_START) {
      if (!parentCaretPosition.endsWith(CaretPositionSuffixTypes.GROUP_START)) {
        newPosition = `${parentCaretPosition}${CaretPositionSuffixTypes.GROUP_START}${CaretPositionSuffixTypes.CARET_INSERTION}`;
      }
    }
    if (mode === CaretPositionSuffixTypes.GROUP_END) {
      newPosition = `${parentCaretPosition}${CaretPositionSuffixTypes.GROUP_END}${CaretPositionSuffixTypes.CARET_INSERTION}`;
    }
    setMorphingCaretPosition(newPosition);
  };

  if (getIsCaretPositionInInitialInsertionMode(morphingCaretPosition)) return null;

  return isCaretActivatedForAction ? (
    <Caret>
      {children({ caretDropdownMode })}
    </Caret>
  ) : (
    <>
      <HiddenCaret
        onClick={onClickHiddenCaret}
        inlineSuggestion={inlineSuggestion}
        isClosingSuggestion={mode === CaretPositionSuffixTypes.GROUP_END}
      >
        {caretInlineSuggestionString}
      </HiddenCaret>
      {inlineSuggestion && mode === CaretPositionSuffixTypes.GROUP_END && (
        <HoverButton
          id="close-group-hover-button"
          onClick={onClickHiddenCaret}
          size={ButtonSize.xsmall}
          variant={ButtonVariant.tertiary}
        >
          close group here
        </HoverButton>
      )}
    </>
  );
}

export default MorphingCaret;
